import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

function Footer() {
  const [companyLanguage, setCompanyLanguage] = useState('en');

  useEffect(() => {
    const storedLang = localStorage.getItem('companyLanguage') || 'en';
    setCompanyLanguage(storedLang);

    const handleStorageChange = () => {
      const updatedLang = localStorage.getItem('companyLanguage') || 'en';
      setCompanyLanguage(updatedLang);
    };

    window.addEventListener('companyDataUpdated', handleStorageChange);

    const interval = setInterval(() => {
      const updatedLang = localStorage.getItem('companyLanguage') || 'en';
      if (updatedLang !== companyLanguage) {
        setCompanyLanguage(updatedLang);
      }
    }, 2000);

    return () => {
      window.removeEventListener('companyDataUpdated', handleStorageChange);
      clearInterval(interval);
    };
  }, [companyLanguage]);

  return (
    <footer
      className={`bg-brand-header text-brand-background mt-16 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
      dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}
    >
      {/* Footer animations (scoped locally) */}
      <style>{`
        @keyframes footerFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
        @keyframes footerGlow {
          0%, 100% { box-shadow: 0 0 0 rgba(250, 204, 21, 0.0); }
          50% { box-shadow: 0 0 28px rgba(250, 204, 21, 0.35); }
        }
        .footer-logo-float { animation: footerFloat 3.2s ease-in-out infinite; }
        .footer-logo-glow { animation: footerGlow 2.8s ease-in-out infinite; }
      `}</style>

      <div className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-4 gap-8">
          {/* About Section */}
          <div>
            <div
              className={`flex items-center gap-3 mb-4 ${companyLanguage === 'ur' ? 'flex-row-reverse' : ''}`}
            >
              <div className="relative group">
                <div className="absolute -inset-2 rounded-2xl bg-brand-button/30 blur-lg opacity-70 group-hover:opacity-100 transition-opacity footer-logo-glow"></div>
                <img
                  src={`${process.env.PUBLIC_URL || ''}/logobest.png`}
                  alt="Logo"
                  className="relative h-24 w-24 md:h-28 md:w-28 object-contain footer-logo-float transform transition-transform duration-300 group-hover:rotate-2 group-hover:scale-[1.05]"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>

              <h3 className="text-2xl font-bold text-brand-button leading-tight">
              {companyLanguage === 'ur' ? 'گلہ منڈی کھاتہ' : 'Mandi Khata'}
            </h3>
            </div>
            <p className="text-brand-background/90 mb-4">
              {companyLanguage === 'ur'
                ? 'گلہ منڈی کے کمیشن ایجنٹس کے لیے ایسا آن لائن سسٹم جو روزنامچہ، بل اور کھاتے کو آسان اور محفوظ بنائے۔'
                : 'An online system for galla mandi commission agents to manage roznamcha, bills and ledgers easily and safely.'}
            </p>
            <div className="flex space-x-4">
              <a href="#" className="text-brand-background/80 hover:text-brand-button transition">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a href="#" className="text-brand-background/80 hover:text-brand-button transition">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                </svg>
              </a>
              <a href="#" className="text-brand-background/80 hover:text-brand-button transition">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Services Section */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-brand-button">
              {companyLanguage === 'ur' ? 'سروسز' : 'Services'}
            </h4>
            <ul className="space-y-2 text-brand-background/80">
              <li>
                <Link to="/services/customer-experience" className="hover:text-brand-button transition">
                  {companyLanguage === 'ur' ? 'کسٹمر سپورٹ' : 'Customer Experience'}
                </Link>
              </li>
              <li>
                <Link to="/services/training-programs" className="hover:text-brand-button transition">
                  {companyLanguage === 'ur' ? 'ٹریننگ پروگرامز' : 'Training Programs'}
                </Link>
              </li>
              <li>
                <Link to="/services/business-strategy" className="hover:text-brand-button transition">
                  {companyLanguage === 'ur' ? 'کاروباری مشاورت' : 'Business Strategy'}
                </Link>
              </li>
              <li>
                <Link to="/services/esg-consulting" className="hover:text-brand-button transition">
                  {companyLanguage === 'ur' ? 'ESG کنسلٹنگ' : 'ESG Consulting'}
                </Link>
              </li>
              <li>
                <Link to="/services/development-hub" className="hover:text-brand-button transition">
                  {companyLanguage === 'ur' ? 'ڈیولپمنٹ ہب' : 'Development Hub'}
                </Link>
              </li>
            </ul>
          </div>

          {/* Resources Section */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-brand-button">
              {companyLanguage === 'ur' ? 'ریسورسز' : 'Resources'}
            </h4>
            <ul className="space-y-2 text-brand-background/80">
              <li>
                <Link to="/contact" className="hover:text-brand-button transition">
                  {companyLanguage === 'ur' ? 'ہم سے رابطہ' : 'Contact us'}
                </Link>
              </li>
              <li>
                <Link to="/team" className="hover:text-brand-button transition">
                  {companyLanguage === 'ur' ? 'ٹیم ممبرز' : 'Team Member'}
                </Link>
              </li>
              <li>
                <Link to="/about" className="hover:text-brand-button transition">
                  {companyLanguage === 'ur' ? 'تعریفیں' : 'Recognitions'}
                </Link>
              </li>
              <li>
                <Link to="/careers" className="hover:text-brand-button transition">
                  {companyLanguage === 'ur' ? 'کریئرز' : 'Careers'}
                </Link>
              </li>
              <li>
                <Link to="/blog" className="hover:text-brand-button transition">
                  {companyLanguage === 'ur' ? 'خبریں' : 'News'}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info Section */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-brand-button">
              {companyLanguage === 'ur' ? 'رابطہ کی معلومات' : 'Contact Info'}
            </h4>
            <ul className="space-y-3 text-brand-background/80">
              <li className="flex items-start">
                <svg className="w-5 h-5 mr-2 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <span>03231099709</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 mr-2 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span>choudharyusama908@gmail.com</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 mr-2 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>993 Renner Burg, West Rond, MT 94251-030</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Newsletter Section */}
        <div className="mt-12 pt-8 border-t border-brand-card/60">
          <div className="max-w-2xl mx-auto">
            <h4 className="text-lg font-semibold mb-4 text-center text-brand-button">
              {companyLanguage === 'ur' ? 'ہماری خبرنامہ کے لیے سبسکرائب کریں' : 'Subscribe to Our Newsletter'}
            </h4>
            <div className="flex gap-4">
              <input
                type="email"
                placeholder={companyLanguage === 'ur' ? 'اپنا ای میل درج کریں' : 'Enter your email'}
                className="flex-1 px-4 py-3 rounded-lg bg-brand-background text-gray-900 border border-brand-card focus:outline-none focus:ring-2 focus:ring-brand-button"
              />
              <button className="bg-brand-button hover:bg-yellow-300 text-gray-900 font-semibold px-6 py-3 rounded-lg transition transform hover:-translate-y-0.5 hover:shadow-lg">
                {companyLanguage === 'ur' ? 'سبسکرائب' : 'Subscribe'}
              </button>
            </div>
            <p className="text-sm text-brand-background/70 mt-2 text-center">
              {companyLanguage === 'ur'
                ? 'کیا آپ ہماری شرائط و ضوابط سے متفق ہیں؟'
                : 'Agree to our Terms & Condition?'}
            </p>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-8 pt-8 border-t border-brand-card/60 text-center text-brand-background/80">
          <p>
            {companyLanguage === 'ur'
              ? '© 2025 گلہ منڈی کھاتہ – تمام حقوق محفوظ ہیں'
              : '© 2025 Mandi Khata – All rights reserved'}
          </p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
